/**
 * SunoTimestampedViewerSheet — App-level karaoke-viewer.
 *
 * Открывается автоматически при pendingViewer в SunoTimestampedJobsContext.
 * Что юзер видит и может делать:
 *   - Аудио-плеер source-трека (native <audio controls>)
 *   - Строки текста с таймкодом слева
 *   - Активная строка подсвечивается в такт playback'а
 *   - Клик по любой строке → трек перематывается на её начало
 *   - Spacebar = play/pause (когда фокус не в input)
 *   - Автоскролл активной строки в viewport
 *
 * При повторном открытии того же трека — виден мгновенно из localStorage-кэша.
 */
import { useEffect, useRef, useState } from 'react'
import Sheet from '../../../components/Sheet'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { showToast } from '../../../components/Toast'
import { useSunoTimestampedJobs } from '../../../context/SunoTimestampedJobsContext'
import { formatTime, activeLineIndex, activeWordIndex, type LyricLine } from '../utils/sunoTimestamped'

export default function SunoTimestampedViewerSheet() {
	const { pendingViewer, dismissViewer } = useSunoTimestampedJobs()
	if (!pendingViewer) return null
	return <ViewerBody viewer={pendingViewer} onClose={dismissViewer} />
}

function ViewerBody({
	viewer,
	onClose,
}: {
	viewer: {
		trackId: string
		title: string | null
		lines: LyricLine[]
		audioUrl: string
		imageUrl: string | null
	}
	onClose: () => void
}) {
	const { title, lines, audioUrl, imageUrl } = viewer
	const audioRef = useRef<HTMLAudioElement>(null)
	const activeLineRef = useRef<HTMLButtonElement>(null)
	const [currentTime, setCurrentTime] = useState(0)

	// Активная строка + «поющееся» слово в ней (пословная подсветка).
	const activeIdx = activeLineIndex(lines, currentTime)
	const activeWordIdx = activeWordIndex(lines[activeIdx], currentTime)

	// Подписка на timeupdate — для подсветки текущей строки.
	useEffect(() => {
		const el = audioRef.current
		if (!el) return
		const onTime = () => setCurrentTime(el.currentTime)
		el.addEventListener('timeupdate', onTime)
		return () => el.removeEventListener('timeupdate', onTime)
	}, [audioUrl])

	// Автоскролл — только при СМЕНЕ активной строки (не на каждый тик:
	// иначе плавная прокрутка дёргается и визуально «отстаёт» от слов).
	useEffect(() => {
		if (activeIdx < 0) return
		activeLineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}, [activeIdx])

	// Spacebar = play/pause.
	useEffect(() => {
		function handleKey(e: KeyboardEvent) {
			if (e.code !== 'Space') return
			const target = e.target as HTMLElement | null
			const tag = target?.tagName
			if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
			const el = audioRef.current
			if (!el || !el.src) return
			e.preventDefault()
			if (el.paused) el.play().catch(() => {})
			else el.pause()
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [])

	const handleLineClick = (line: LyricLine) => {
		const el = audioRef.current
		if (!el) return
		el.currentTime = line.start
		el.play().catch(() => {})
	}

	const copyAllText = async () => {
		try {
			await navigator.clipboard.writeText(lines.map((l) => l.text).join('\n'))
			showToast('Текст скопирован', 'success')
		} catch {
			showToast('Не удалось скопировать', 'error')
		}
	}

	const copyWithTimecodes = async () => {
		try {
			const body = lines
				.map((l) => `[${formatTime(l.start)}] ${l.text}`)
				.join('\n')
			await navigator.clipboard.writeText(body)
			showToast('Текст с таймкодами скопирован', 'success')
		} catch {
			showToast('Не удалось скопировать', 'error')
		}
	}

	const downloadLrc = () => {
		// LRC-формат: [mm:ss.xx]line — поддерживается большинством плееров.
		const lrc = lines
			.map((l) => {
				const mm = Math.floor(l.start / 60)
				const ss = (l.start % 60).toFixed(2).padStart(5, '0')
				return `[${mm.toString().padStart(2, '0')}:${ss}]${l.text}`
			})
			.join('\n')
		const blob = new Blob([lrc], { type: 'text/plain;charset=utf-8' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		const safe = (title?.trim() || 'lyrics').replace(/[\\/:"*?<>|]/g, '_').slice(0, 100)
		a.download = `${safe}.lrc`
		document.body.appendChild(a)
		a.click()
		a.remove()
		setTimeout(() => URL.revokeObjectURL(url), 10_000)
	}

	return (
		<Sheet
			open={true}
			onClose={onClose}
			variant="center"
			title="Текст с таймкодами"
			className="suno-timestamped-viewer"
		>
			<div className="suno-timestamped-viewer__body">
				<div className="suno-timestamped-viewer__header">
					{imageUrl && (
						<img
							src={imageUrl}
							alt=""
							className="suno-timestamped-viewer__cover"
							loading="lazy"
						/>
					)}
					<div className="suno-timestamped-viewer__meta">
						<div className="suno-timestamped-viewer__title">
							{title?.trim() || 'Без названия'}
						</div>
						<div className="suno-timestamped-viewer__subtitle">
							{lines.length} строк · {formatTime(lines[lines.length - 1]?.end ?? 0)}
						</div>
					</div>
				</div>

				{audioUrl ? (
					<audio
						ref={audioRef}
						src={audioUrl}
						controls
						preload="metadata"
						className="suno-timestamped-viewer__audio"
					/>
				) : (
					<div className="suno-timestamped-viewer__no-audio">
						Аудио-плеер недоступен (трек без audio_url). Текст ниже всё равно доступен.
					</div>
				)}

				<p className="suno-timestamped-viewer__hint">
					Нажми <kbd>Space</kbd> — play/pause · клик по строке — перейти к ней
				</p>

				<div className="suno-timestamped-viewer__lines" role="list">
					{lines.map((line, i) => {
						const isActive = i === activeIdx
						return (
							<button
								key={i}
								type="button"
								ref={isActive ? activeLineRef : null}
								role="listitem"
								className={`suno-timestamped-viewer__line${isActive ? ' suno-timestamped-viewer__line--active' : ''}`}
								onClick={() => handleLineClick(line)}
								title={`Перейти к ${formatTime(line.start)}`}
							>
								<span className="suno-timestamped-viewer__line-time">
									{formatTime(line.start)}
								</span>
								<span className="suno-timestamped-viewer__line-text">
									{isActive && line.words?.length
										? line.words.map((w, wi) => (
											<span
												key={wi}
												className={`suno-timestamped-viewer__word${
													wi === activeWordIdx
														? ' suno-timestamped-viewer__word--current'
														: wi < activeWordIdx
															? ' suno-timestamped-viewer__word--sung'
															: ''
												}`}
											>
												{wi > 0 ? ' ' : ''}{w.word}
											</span>
										))
										: line.text}
								</span>
							</button>
						)
					})}
				</div>

				<div className="suno-timestamped-viewer__actions">
					<button
						type="button"
						className="suno-timestamped-viewer__btn"
						onClick={copyAllText}
						title="Скопировать только текст песни (без таймкодов)"
					>
						<Icon id="gen-copy" size={12} />
						Текст
					</button>
					<button
						type="button"
						className="suno-timestamped-viewer__btn"
						onClick={copyWithTimecodes}
						title="Скопировать текст со временем каждой строки"
					>
						<Icon id="gen-copy" size={12} />
						С таймкодами
					</button>
					<button
						type="button"
						className="suno-timestamped-viewer__btn suno-timestamped-viewer__btn--primary"
						onClick={downloadLrc}
						title="Скачать .lrc — формат караоке, поддерживается плеерами"
					>
						<Icon id="gen-download" size={12} />
						.lrc
					</button>
				</div>
			</div>
		</Sheet>
	)
}
